import React, { FunctionComponent, ReactElement } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CartRadioInputLabelInfos from "@Components/atoms/cart/CartRadioInputLabelInfos";
import CartPaymentForm from "@Components/molecules/cart/CartPaymentForm";
import CartRadioInputWithLabel from "@Components/molecules/cart/CartRadioInputWithLabel";
import { PaymentMethods } from "@Types/cart";
import CreditCard from "@Types/creditCard";
import bancontact from "./images/bancontact.png";
import cbGie from "./images/cb-gie.png";
import cbMastercard from "./images/cb-mastercard.png";
import cbVisa from "./images/cb-visa.png";
import giropay from "./images/giropay.png";
import klarna from "./images/klarna.png";
import paypal from "./images/paypal.png";
import sofort from "./images/sofort.png";
import "./style.scss";

interface Props {
  availablePayments: PaymentMethods[];
  displayProcessout: boolean;
  onChangeCreditCard: () => void;
  onChangePaymentMethod: (name: PaymentMethods) => void;
  onSaveCreditCard: () => void;
  saveCreditCard: boolean;
  savedCreditCard?: CreditCard;
  selectedMethod: PaymentMethods;
}

const CartPaymentContent: FunctionComponent<Props> = ({
  onChangePaymentMethod,
  onChangeCreditCard,
  onSaveCreditCard,
  selectedMethod,
  saveCreditCard,
  savedCreditCard,
  availablePayments,
}): ReactElement => {
  const intl = useIntl();
  const handleChangePaymentMethod = (method: PaymentMethods) => {
    if (availablePayments.includes(method)) {
      onChangePaymentMethod(method);
    }
  };

  return (
    <div className="CartPaymentContent">
      {availablePayments.includes("cb") && (
        <CartRadioInputWithLabel
          value="cb"
          name="payment-method"
          checked={selectedMethod === "cb"}
          onChange={() => handleChangePaymentMethod("cb")}
        >
          <CartRadioInputLabelInfos
            title={intl.formatMessage({ id: "cheerz_CartPaymentContent_cb_title" })}
            logos={[
              {
                name: "visa",
                src: cbVisa,
              },
              {
                name: "mastercard",
                src: cbMastercard,
              },
              {
                name: "cb",
                src: cbGie,
              },
            ]}
          >
            {selectedMethod === "cb" &&
            savedCreditCard &&
            savedCreditCard.month !== undefined &&
            savedCreditCard.year !== undefined &&
            savedCreditCard.identifier !== undefined ? (
              <>
                <div className="CartPaymentContent-card-info">
                  <FormattedMessage id={"cheerz_CartPaymentContent_cb_expired_date"} />{" "}
                  {savedCreditCard.month}/{savedCreditCard.year}
                </div>
                <div className="CartPaymentContent-card-info">
                  XXXX-XXXX-XXXX-{savedCreditCard.identifier}
                </div>
                <div
                  className="CartPaymentContent-card-info CartPaymentContent-updateCard"
                  onClick={onChangeCreditCard}
                >
                  <FormattedMessage id={"cheerz_CartPaymentContent_cb_change"} />
                </div>
              </>
            ) : (
              <CartPaymentForm
                onSaveCreditCard={onSaveCreditCard}
                saveCreditCard={saveCreditCard}
              />
            )}
          </CartRadioInputLabelInfos>
        </CartRadioInputWithLabel>
      )}
      {availablePayments.includes("paypal") && (
        <CartRadioInputWithLabel
          value="paypal"
          name="payment-method"
          checked={selectedMethod === "paypal"}
          onChange={() => handleChangePaymentMethod("paypal")}
        >
          <CartRadioInputLabelInfos
            title={intl.formatMessage({ id: "cheerz_CartPaymentContent_paypal_title" })}
            logos={[
              {
                name: "paypal",
                src: paypal,
              },
            ]}
          >
            <FormattedMessage id={"cheerz_CartPaymentContent_paypal_description"} />
          </CartRadioInputLabelInfos>
        </CartRadioInputWithLabel>
      )}
      {availablePayments.includes("sofort") && (
        <CartRadioInputWithLabel
          value="sofort"
          name="payment-method"
          checked={selectedMethod === "sofort"}
          onChange={() => handleChangePaymentMethod("sofort")}
        >
          <CartRadioInputLabelInfos
            title={intl.formatMessage({ id: "cheerz_CartPaymentContent_sofort_title" })}
            logos={[
              {
                name: "sofort",
                src: sofort,
              },
            ]}
          >
            <FormattedMessage id={"cheerz_CartPaymentContent_sofort_description"} />
          </CartRadioInputLabelInfos>
        </CartRadioInputWithLabel>
      )}
      {availablePayments.includes("bancontact") && (
        <CartRadioInputWithLabel
          value="bancontact"
          name="payment-method"
          checked={selectedMethod === "bancontact"}
          onChange={() => handleChangePaymentMethod("bancontact")}
        >
          <CartRadioInputLabelInfos
            title={intl.formatMessage({ id: "cheerz_CartPaymentContent_bancontact_title" })}
            logos={[
              {
                name: "bancontact",
                src: bancontact,
              },
            ]}
          >
            <FormattedMessage id={"cheerz_CartPaymentContent_beaucontact_description"} />
          </CartRadioInputLabelInfos>
        </CartRadioInputWithLabel>
      )}
      {availablePayments.includes("giropay") && (
        <CartRadioInputWithLabel
          value="giropay"
          name="payment-method"
          checked={selectedMethod === "giropay"}
          onChange={() => handleChangePaymentMethod("giropay")}
        >
          <CartRadioInputLabelInfos
            title={intl.formatMessage({ id: "cheerz_CartPaymentContent_giropay_title" })}
            logos={[
              {
                name: "giropay",
                src: giropay,
              },
            ]}
          >
            <FormattedMessage id={"cheerz_CartPaymentContent_giropay_description"} />
          </CartRadioInputLabelInfos>
        </CartRadioInputWithLabel>
      )}
      {availablePayments.includes("klarna") && (
        <CartRadioInputWithLabel
          value="klarna"
          name="payment-method"
          checked={selectedMethod === "klarna"}
          onChange={() => handleChangePaymentMethod("klarna")}
        >
          <CartRadioInputLabelInfos
            title={intl.formatMessage({ id: "cheerz_CartPaymentContent_klarna_title" })}
            logos={[
              {
                name: "klarna",
                src: klarna,
              },
            ]}
          >
            <FormattedMessage id={"cheerz_CartPaymentContent_klarna_description"} />
          </CartRadioInputLabelInfos>
        </CartRadioInputWithLabel>
      )}
    </div>
  );
};

export default CartPaymentContent;
